import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useContext } from "react";
import { css } from "@emotion/css";
import Handsontable from "handsontable";
import { HotTable } from "@handsontable/react";
import { Label, PrimaryButton, Stack, TextField, Text } from "@fluentui/react";

import "handsontable/dist/handsontable.min.css";
import { SLCreateLineItemSchema } from "../../../schemas/SLCreateLineItemSchema";
import { SLCreateSLBatchContext } from "../../../contexts/SLCreateSLBatchContext";
import { ISLEntryResponse } from "../SLCreateSchemas";
import { SLLoadingStatus } from "../../../utilities/utilities";

import {
  getMapColIndexToLineItemNamesInOrderFromApi,
  LineItemsResponse,
  getLineItemsHeaders
} from "./LineItemsSchemas";
import {
  validateAndFormatAllLineItems,
  formatCellInPlaceAndValidateAllLineItems,
  validateLineItems
} from "./LineItemsValidation";
import { For } from "react-loops";
import { getHandsontableChangesStream, getHandsontableData } from "./LineItemsStreams";
import { IErrorTypes } from "./LineItemsValidationUtilities";
import { AccountTypeOptions } from "./LineItemsUtilities";
import { default as numeral } from "numeral";

const styles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-content: center;

  & .entry-line-grid--label {
    align-self: flex-start;
  }

  & .entry-line-grid--container {
    display: block;
    width: 100%;
    position: relative;
    min-height: 200px;
    color: var(--main-font-color, black);
  }

  & .entry-line-grid--row:first-of-type {
    width: 100%;
    overflow: hidden;
  }

  & .entry-line-grid--row:nth-of-type(2) {
    margin-top: 8px;
    float: right;
  }

  & .entry-line-grid--reset-button {
    background-color: var(--main-bg-color, gray);
  }
`;

export interface ILineItemGridInterface {
  key: string;
  item: SLCreateLineItemSchema;
}

function setupHandsonTable(
  table: Handsontable,
  setLineItems: React.Dispatch<React.SetStateAction<string[][]>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, unknown> | IErrorTypes>>
) {
  const bigChangesStream = getHandsontableData(table);
  bigChangesStream.subscribe((data) => {
    setLineItems(() => {
      return validateAndFormatAllLineItems(setErrors, data);
    });
  });
  const smallEditsStream = getHandsontableChangesStream(table);
  smallEditsStream.subscribe(({ row, column, currentVal }) => {
    setLineItems((lineItems) => {
      if (lineItems.length === 0 || row >= lineItems.length) {
        const filteredData = table.getData().slice(0, row + 1);
        return validateAndFormatAllLineItems(setErrors, filteredData);
      } else {
        const items = [...lineItems];
        formatCellInPlaceAndValidateAllLineItems(setErrors, items, { row, column, currentVal });
        return items;
      }
    });
  });
}

function lineItemsToArray(values: LineItemsResponse[]) {
  const correctOrder = getMapColIndexToLineItemNamesInOrderFromApi();
  const lineItemsList = values.reduce((lItems: string[][], lineItem: LineItemsResponse) => {
    const row = correctOrder.map((propertyName) => lineItem[propertyName]);
    lItems.push(row as string[]);
    return lItems;
  }, [] as string[][]);
  return lineItemsList;
}

export interface ILineItemsGridRef {
  getLineItems: () => string[][];
  setLineItems: (lineItems: string[][]) => void;
  reset: () => void;
}

interface HandsontableOptions {
  readOnly: boolean;
}

function getHandstonTableConfiguration(opts: HandsontableOptions): Handsontable.GridSettings {
  const Headers = getLineItemsHeaders();
  const defaultInitialRowSize = 901;
  return {
    minRows: defaultInitialRowSize,
    minCols: Headers.length,
    minSpareRows: 1,
    colHeaders: Headers,
    renderer: "html",
    colWidths: 100,
    readOnly: opts.readOnly,
    columns: [
      {
        type: "dropdown",
        source: AccountTypeOptions.slice(1).map((keyValue) => `${keyValue.text}`)
      },
      ...Headers.slice(1).map(() => ({}))
    ],
    rowHeaders: true,
    columnSorting: true,
    stretchH: "all",
    licenseKey: "non-commercial-and-evaluation",
    contextMenu: {
      items: {
        remove_row: {}
      }
    }
  };
}

export interface ILineItemsGridProps {
  readOnly: boolean;
}

const LineItems = forwardRef((props: ILineItemsGridProps, ref: React.Ref<ILineItemsGridRef>) => {
  const GridReference: React.RefObject<HotTable> = useRef(null);
  const slCreateContext = useContext(SLCreateSLBatchContext);
  const [lineItems, _setLineItems] = useState<string[][]>([]);
  const [errors, _setErrors] = useState<IErrorTypes | Record<string, unknown>>({});

  useImperativeHandle(ref, () => ({
    // Make sure Line Items returns numbers on Document Amount, we will only be doing Number(DocumentAmount) past this point
    getLineItems() {
      const items = lineItems.map((row) => {
        if (row[10] && row[10] !== "") {
          row[10] = `${numeral(row[10]).value()}`;
        }
        return row;
      });
      return items;
    },
    setLineItems(lItems: string[][]) {
      const items = validateAndFormatAllLineItems(_setErrors, lItems);
      _setLineItems(items);
    },
    reset() {
      _setLineItems([]);
    }
  }));

  useEffect(() => {
    const noErrorsFound = Object.keys(errors).every((key: string) => errors[key as keyof typeof errors] === "");
    if (slCreateContext.loadingStatus === SLLoadingStatus.Resolved) {
      slCreateContext.setErrors({
        collectorName: "LineItems",
        value: !noErrorsFound
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if (slCreateContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (slCreateContext.entry) {
        const entry = slCreateContext.entry as ISLEntryResponse;
        const items = validateAndFormatAllLineItems(_setErrors, lineItemsToArray(entry.lineItems));
        _setLineItems(items);
      } else {
        validateLineItems(_setErrors, []);
        _setLineItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slCreateContext.loadingStatus, slCreateContext.entry]);

  useEffect(() => {
    if (GridReference.current !== null) {
      const table: Handsontable = GridReference.current.hotInstance;
      setupHandsonTable(table, _setLineItems, _setErrors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GridReference]);

  const onResetButtonClick = () => {
    _setLineItems([]);
  };

  let rowsToShow = lineItems.length > 25 ? 25 : lineItems.length;
  rowsToShow = rowsToShow <= 25 ? 25 : rowsToShow;
  const h = lineItems.length === 0 ? Math.ceil(23 * (10 + 1)) : Math.ceil(23 * (rowsToShow + 1));
  return (
    <>
      {Object.keys(errors).length !== 0 ? (
        <>
          <h4
            style={{
              color: "var(--error-font-color, red)",
              margin: 0
            }}
          >
            Errors in Line Items
          </h4>
          <ul
            style={{
              color: "var(--error-font-color, red)",
              margin: 0
            }}
          >
            <For of={Object.keys(errors)}>
              {(theErrorType) => {
                const msg = (errors as IErrorTypes)[theErrorType as keyof IErrorTypes];
                if (!msg) {
                  return null;
                }
                return (
                  <li>
                    <Text variant="medium">{msg}</Text>
                  </li>
                );
              }}
            </For>
          </ul>
        </>
      ) : null}
      <Stack horizontal tokens={{ childrenGap: 18, padding: "m" }} horizontalAlign="space-between">
        <Stack.Item grow={2}>
          <Label htmlFor="lineItemsCount">Line Items count</Label>
          <TextField id="lineItemsCount" disabled value={`${lineItems.length}`} />
        </Stack.Item>
        <Stack.Item>
          <Label htmlFor="lineItemsStatus">Read Only</Label>
          <TextField id="lineItemsStatus" disabled value={props.readOnly ? "true" : "false"} />
        </Stack.Item>
      </Stack>
      <div className={styles}>
        <div className="entry-line-grid--container">
          <div className="entry-line-grid--row">
            {/* Make a copy of line items because otherwise Handsontable will ruin the state */}
            <HotTable
              ref={GridReference}
              data={[...lineItems]}
              height={h}
              settings={getHandstonTableConfiguration({ readOnly: props.readOnly })}
            />
          </div>
          <div className="entry-line-grid--row">
            <PrimaryButton
              text="Reset"
              onClick={onResetButtonClick}
              className="entry-line-grid--reset-button"
              disabled={props.readOnly}
            />
          </div>
        </div>
      </div>
    </>
  );
});
LineItems.displayName = "LineItems";

export default LineItems;
